import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SubscriptionUsageResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';

/**
 * Fetches authoritative subscription usage from the server (licensed users for
 * the app subscription; devices with fall/tilt/geofence effectively enabled for
 * the LWD subscription), rather than counting users/devices on the client.
 */
export function useFetchSubscriptionUsage(
    companyId: string,
    options?: { enabled?: boolean }
): {
    status: 'error' | 'success' | 'pending';
    usage: SubscriptionUsageResponseData | undefined;
    refetch: () => void;
} {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { status, data, refetch } = useQuery({
        queryKey: ['fetchSubscriptionUsage', companyId],
        queryFn: () => {
            return openApiRef.current.companies
                .getSubscriptionUsage(companyId)
                .then((response) => response.data.data);
        },
        enabled: options?.enabled ?? true
    });

    return {
        status,
        usage: data,
        refetch: () => {
            void refetch();
        }
    };
}
